import { useState } from "react";
import { Badge, Button, Card, EmptyState, MUTED_TEXT, Select, Spinner, TextArea, TextField, stackStyle } from "@dayvise/react-ui";
import { useAgents, useAutomationTemplateMutations, useAutomationTemplates } from "./hooks.ts";

interface ParameterDraft {
  readonly key: string;
  readonly label: string;
}

function parseParameters(raw: string): readonly ParameterDraft[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [key, ...rest] = line.split("=");
      return { key: (key ?? "").trim(), label: rest.join("=").trim() || (key ?? "").trim() };
    })
    .filter((entry) => entry.key.length > 0);
}

/**
 * Versioned automation templates. A template is a prompt plus its schedule
 * shape; using one creates a concrete schedule through the normal quota and
 * authorization checks — nothing here bypasses them.
 */
export function AutomationTemplatesPanel() {
  const query = useAutomationTemplates();
  const agents = useAgents();
  const mutations = useAutomationTemplateMutations();
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState<"cron" | "runAt">("cron");
  const [cron, setCron] = useState("");
  const [runAt, setRunAt] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [parameters, setParameters] = useState("");
  const [useTemplateId, setUseTemplateId] = useState<string | null>(null);
  const [useAgentId, setUseAgentId] = useState("");
  const [useTitle, setUseTitle] = useState("");
  const templates = query.data?.templates ?? [];
  const agentList = agents.data?.agents ?? [];
  const error = query.error ?? mutations.create.error ?? mutations.use.error ?? mutations.remove.error;

  const create = () => {
    const parsed = parseParameters(parameters);
    mutations.create.mutate(
      {
        name: name.trim(),
        prompt: prompt,
        ...(kind === "cron" ? { cron: cron.trim() } : { runAt: runAt.trim() }),
        timezone: timezone.trim() || "UTC",
        parameters: parsed.map((entry) => ({ key: entry.key, label: entry.label, required: true })),
      },
      {
        onSuccess: () => {
          setName("");
          setPrompt("");
          setCron("");
          setRunAt("");
          setParameters("");
        },
      },
    );
  };

  return (
    <Card
      title="Automation templates"
      subtitle="Reusable, versioned automations. Using a template creates a real schedule with the chosen agent; creating one does not schedule anything by itself."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {query.isPending ? <Spinner label="Loading templates" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Template action failed"}</p> : null}
        <TextField label="Template name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Weekly release digest" />
        <TextArea label="Prompt" value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={4} placeholder="Summarise the last week of activity for the team." />
        <Select
          label="Schedule shape"
          value={kind}
          options={[
            { value: "cron", label: "Recurring (cron)" },
            { value: "runAt", label: "One-off (date and time)" },
          ]}
          onChange={(event) => setKind(event.target.value as "cron" | "runAt")}
        />
        {kind === "cron" ? (
          <TextField label="Cron expression" value={cron} onChange={(event) => setCron(event.target.value)} placeholder="0 9 * * 1" />
        ) : (
          <TextField label="Run at" value={runAt} onChange={(event) => setRunAt(event.target.value)} placeholder="2026-10-10T09:00:00Z" />
        )}
        <TextField label="Timezone" value={timezone} onChange={(event) => setTimezone(event.target.value)} placeholder="UTC" />
        <TextArea
          label="Parameters (one per line: key = label)"
          value={parameters}
          onChange={(event) => setParameters(event.target.value)}
          rows={3}
          placeholder={"repo = repository to inspect\nchannel = destination channel"}
        />
        <div>
          <Button onClick={create} disabled={mutations.create.isPending || name.trim().length === 0 || prompt.trim().length === 0}>
            {mutations.create.isPending ? "Creating…" : "Create template"}
          </Button>
        </div>
        {!query.isPending && templates.length === 0 ? (
          <EmptyState title="No templates yet" description="A template captures a prompt and its schedule so it can be reused without retyping." />
        ) : null}
        {templates.map((template) => (
          <div key={template.templateId} style={stackStyle("var(--dayvise-space-xs)")}>
            <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
              <strong>{template.name}</strong>
              <Badge tone="neutral">v{template.version}</Badge>
              <Badge tone="neutral">{template.cronExpression ? `cron ${template.cronExpression}` : template.runAt ? `once at ${template.runAt}` : "unscheduled"}</Badge>
              {template.parameters.length > 0 ? <Badge tone="neutral">{template.parameters.length} parameter(s)</Badge> : null}
            </div>
            {useTemplateId === template.templateId ? (
              <div style={stackStyle("var(--dayvise-space-xs)")}>
                <Select
                  label="Agent"
                  value={useAgentId}
                  options={[
                    { value: "", label: "Choose an agent…" },
                    ...agentList.map((agent) => ({ value: agent.agentId, label: agent.name })),
                  ]}
                  onChange={(event) => setUseAgentId(event.target.value)}
                />
                <TextField label="Schedule title" value={useTitle} onChange={(event) => setUseTitle(event.target.value)} placeholder={`${template.name} (from template)`} />
                <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                  <Button
                    disabled={mutations.use.isPending || useAgentId.length === 0}
                    onClick={() => {
                      mutations.use.mutate(
                        { templateId: template.templateId, agentId: useAgentId, title: useTitle.trim().length > 0 ? useTitle.trim() : undefined },
                        { onSuccess: () => setUseTemplateId(null) },
                      );
                    }}
                  >
                    {mutations.use.isPending ? "Creating schedule…" : "Create schedule"}
                  </Button>
                  <Button onClick={() => setUseTemplateId(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                <Button onClick={() => setUseTemplateId(template.templateId)}>Use…</Button>
                <Button onClick={() => mutations.remove.mutate(template.templateId)} disabled={mutations.remove.isPending}>
                  Delete
                </Button>
              </div>
            )}
            {mutations.use.data && "templateId" in mutations.use.data && mutations.use.data.templateId === template.templateId ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>Schedule {mutations.use.data.scheduleId} created.</p>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}
